import {
  BRAND_CAMPAIGN_DOC_PATH,
  BRAND_DOC_PATH,
  BRAND_CAMPAIGN_DASHBOARD_STAT_LOGS_COL_PATH,
  BRAND_CAMPAIGN_OFF_PLATFORM_RELATIONSHIPS_COL_PATH,
  BRAND_CAMPAIGN_USER_RELATIONSHIPS_COL_PATH,
  AGENCY_DOC_PATH,
  EXTERNAL_PLATFORM_TRACK_HISTORY_COL_PATH,
  EXTERNAL_PLATFORM_TRACK_DOC_PATH,
  EXTERNAL_POSTS_COL_PATH,
  BRAND_CAMPAIGN_GROUP_DOC_PATH,
  REPORT_DOC_PATH,
} from '@happstv/shared/util/firebase/firestorePaths'

import { 
  getCol,
  getDoc,
  watchCol,
} from '@happstv/shared/util/firebase/firestoreUtils'

import {
  CAMPAIGN_SUBMITTABLE_TYPES,
  getExternalPlatformBySubmittableType,
  getRelevantStatsList,
  getSubmissionTypeFromCampaign,
  getUniqueSubmissionString,
} from '@happstv/shared/util/brands/brandCampaignUtils'

import { ONE_MONTHISH } from '@happstv/shared/util/timeConstants'

import { date, timestamp, statHourStartDate, nestedObjectAssignWithOptions, filterObjectKeys } from '@happstv/shared/util/utils'

import { TIKTOK_NATIVE } from '@happstv/shared/util/multicastingConstants'

// Helper functions
const aggregateCurrentStatList = (statList) => {
  return nestedObjectAssignWithOptions({ sumNumbers: true }, {}, ...statList)
}

const aggregateHistoricalStatListList = (statListList, selectedStatLogHoursOfDay) => {
  const result = []

  const minHourNumber = Math.min(...statListList.map(statList => Math.min(...statList.map(stat => stat.logTiming.hourNumber || 0))))
  const maxHourNumber = Math.max(...statListList.map(statList => Math.max(...statList.map(stat => stat.logTiming.hourNumber || 0))))

  for (let hourNumber = minHourNumber; hourNumber <= maxHourNumber; hourNumber += 1) {
    const hourStartDate = statHourStartDate(hourNumber)
    if (selectedStatLogHoursOfDay.includes(hourNumber % 24) && hourStartDate < new Date()) {
      const logsToAdd = statListList
        .map((statList) => {
          return statList
            .filter(({ logTiming = {} }) => logTiming.hourNumber <= hourNumber)
            .reverse()[0]
        })
        .filter(log => log)
      const newDataPoint = aggregateCurrentStatList(logsToAdd)
      result.push({ ...newDataPoint, logTiming: { date: hourStartDate } })
    }
  }

  return result
}


const ensureUpAndToTheRight = (logList) => {
  const reversedOriginal = [...logList].reverse()
  const reversedResult = []

  const originalNextNumericalValues = {}
  let nextNumericalValues = {}
  reversedOriginal.forEach((originalStatLog) => {
    const statLog = { ...originalStatLog }
    const newNextNumericalValues = Object.keys(nextNumericalValues).reduce((prevValue, key) => ({ ...prevValue, [key]: 0 }), {})
    Object.keys(statLog).forEach((key) => {
      const originalValue = statLog[key]
      if (typeof originalValue === 'number') {
        if (nextNumericalValues[key] !== undefined && originalNextNumericalValues[key] !== undefined) {
          statLog[key] = originalNextNumericalValues[key] ? Math.round(nextNumericalValues[key] * Math.min(1, originalValue / originalNextNumericalValues[key])) : 0
        }
        newNextNumericalValues[key] = statLog[key]
        originalNextNumericalValues[key] = originalValue
      }
    })
    reversedResult.push(statLog)
    nextNumericalValues = newNextNumericalValues
  })

  return reversedResult.reverse()
}

const getAverage = array => array.reduce((a, b) => a + b) / array.length

const EXTERNAL_METRIC_SMOOTHING_COUNT = 4
const EXTERNAL_METRIC_SMOOTHING_KEY_LIST = ['viewCount', 'likeCount', 'commentCount', 'shareCount', 'createCount']

const getExternalTrackHistoryInner = async (externalPlatform, soundId) => {
  const TARGET_STAT_LOG_HOURS = [3, 9, 15, 21]
  const selectedStatLogHours = TARGET_STAT_LOG_HOURS

  // Get sound history data
  const soundStatLogCol = await getCol(EXTERNAL_PLATFORM_TRACK_HISTORY_COL_PATH(externalPlatform, soundId), q => q
    .where('logTiming.hourOfDay', 'in', selectedStatLogHours)
    .orderBy('logTiming.date', 'asc'))
  const soundStatLogs = (soundStatLogCol.docs || []).map(doc => doc.data() || {})

  // Filters each stat log
  // For any metric, if at least one of the last five & next five logs are greater than the current, filter out the current
  const adjustedStatLogs = soundStatLogs.map((statLog, i) => {
    const lastWindow = soundStatLogs.slice(Math.max(0, i - EXTERNAL_METRIC_SMOOTHING_COUNT), i)
    const nextWindow = soundStatLogs.slice(i + 1, i + 1 + EXTERNAL_METRIC_SMOOTHING_COUNT)

    const result = { ...statLog }

    EXTERNAL_METRIC_SMOOTHING_KEY_LIST.forEach((key) => {
      const lastValues = lastWindow.map(log => log[key]).filter(value => value !== undefined)
      const nextValues = nextWindow.map(log => log[key]).filter(value => value !== undefined)
      const currentValue = statLog[key]

      if (!lastValues.length || !nextValues.length) return

      const countToAverageNextAndLast = Math.min(lastValues.length, nextValues.length)

      result[key] = getAverage([...lastValues.slice(-countToAverageNextAndLast), currentValue, ...nextValues.slice(0, countToAverageNextAndLast)])
    })

    return result
  })

  return adjustedStatLogs
}

const initState = () => ({
  dashboardLoading: true,
  externalTrackLoading: false,
  coverInfo: undefined,
  aggregateCampaignCohortStats: undefined,
  aggregateCohortProjectionStats: undefined,
  aggregateCampaignStatLogs: undefined,
  topSubmissionsList: undefined,
  externalTrack: undefined,
  externalTrackData: undefined,
  submissionFeedbackUnsubscribe: undefined,
  submissionFeedbackData: undefined,
  soundStatLogs: undefined,
  customGroupTitle: undefined,
  draftsPendingBrandReview: undefined,
  soundTopPostList: undefined,

  brandCampaignList: undefined,
  soundList: undefined,

  showCampaignUi: false,
  showSoundUi: false,
})

export default {
  namespaced: true,
  state: {
    ...initState(),
  },
  mutations: {
    REINIT(state) {
      Object.assign(state, initState())
    },
    SET_DASHBOARD_LOADING(state, payload) {
      state.dashboardLoading = payload
    },
    SET_EXTERNAL_TRACK_LOADING(state, payload) {
      state.externalTrackLoading = payload
    },
    SET_COVER_INFO(state, payload) {
      state.coverInfo = payload
    },
    SET_CAMPAIGN_COHORT_STATS(state, payload) {
      state.aggregateCampaignCohortStats = payload
    },
    SET_SOUND_COHORT_STATS(state, payload) {
      state.aggregateSoundCohortStats = payload
    },
    SET_COHORT_PROJECTION_STATS(state, payload) {
      state.aggregateCohortProjectionStats = payload
    },
    SET_CAMPAIGN_STAT_LOGS(state, payload) {
      state.aggregateCampaignStatLogs = payload
    },
    SET_TOP_SUBMISSIONS_LIST(state, payload) {
      state.topSubmissionsList = payload
    },
    SET_DRAFTS_PENDING_BRAND_REVIEW(state, payload) {
      state.draftsPendingBrandReview = payload
    },
    SET_EXTERNAL_TRACK(state, payload) {
      state.externalTrack = payload
    },
    SET_EXTERNAL_TRACK_DATA(state, payload) {
      state.externalTrackData = payload
    },
    SET_FEEDBACK_UNSUBSCRIBE(state, payload) {
      state.submissionFeedbackUnsubscribe = payload
    },
    SET_FEEDBACK_DATA(state, payload) {
      state.submissionFeedbackData = payload
    },
    SET_SOUND_STAT_LOGS(state, payload) {
      state.soundStatLogs = payload
    },
    SET_CUSTOM_GROUP_TITLE(state, payload) {
      state.customGroupTitle = payload
    },
    SET_SOUND_TOP_POST_LIST(state, payload) {
      state.soundTopPostList = payload
    },
    SET_BRAND_CAMPAIGN_LIST(state, payload) {
      state.brandCampaignList = payload
    },
    SET_SOUND_LIST(state, payload) {
      state.soundList = payload
    },
    SET_SHOW_CAMPAIGN_UI(state, payload) {
      state.showCampaignUi = payload
    },
    SET_SHOW_SOUND_UI(state, payload) {
      state.showSoundUi = payload
    },
  },
  actions: {
    REINIT({ commit }) {
      commit('REINIT')
    },
    async getCampaignDashboardData({ commit, dispatch }, { brandCampaignId, brandCampaignGroupId }) {
      commit('SET_DASHBOARD_LOADING', true)

      let brandCampaignGroup
      let brandCampaignIdList
      if (brandCampaignGroupId) {
        const brandCampaignGroupDoc = await getDoc(BRAND_CAMPAIGN_GROUP_DOC_PATH(brandCampaignGroupId))
        brandCampaignGroup = { id: brandCampaignGroupDoc.id, ...(brandCampaignGroupDoc.data() || {}) }
        brandCampaignIdList = brandCampaignGroup.brandCampaignIds
      } else if (brandCampaignId) {
        brandCampaignIdList = [brandCampaignId]
      }

      if (!brandCampaignIdList?.length) {
        return { success: false, error: 'No campaigns found.' }
      }


      const allCurrentCampaignStats = []
      const allCurrentCampaignProjectionStats = []
      const campaignStatLogList = []
      const TARGET_STAT_LOG_HOURS = [3, 9, 15, 21]
      const TARGET_STAT_LOG_HOURS_BACKOFF = [3, 15]
      let selectedStatLogHours


      const coverInfoPromise = (async () => {
        const firstBrandCampaignDoc = await getDoc(BRAND_CAMPAIGN_DOC_PATH(brandCampaignIdList[0]))
        const firstBrandCampaign = { id: firstBrandCampaignDoc.id, ...(firstBrandCampaignDoc.data() || {}) }

        const result = {}

        // important that this be called fullName, so CampaignPhoto treats it right, etc
        result.fullName = brandCampaignGroup?.customTitle
          || firstBrandCampaign.dashboardName
          || firstBrandCampaign.fullName

        const normalPropagationFields = [
          'profilePhotoPath',
          'targetViewCount',
          'projectedViewCount',
          'brandId',
          'ownerAgencyAgent',
          'chartStartDate',
          'chartEndDate',
          'showDashboardProjections',
          'showTopPostsOnDashboard',
          'showSoundTopPostsOnDashboard',
          'sponsorName',
        ]
        normalPropagationFields.forEach((field) => {
          result[field] = brandCampaignGroup?.[field]
            || firstBrandCampaign[field]
        })

        const { agencyId } = result.ownerAgencyAgent || {}
        if (agencyId) {
          const agencyDoc = await getDoc(AGENCY_DOC_PATH(agencyId))
          result.agency = { id: agencyDoc.id, ...(agencyDoc.data() || {}) }
        }

        const { brandId } = result
        if (brandId) {
          const brandDoc = await getDoc(BRAND_DOC_PATH(brandId))
          result.brand = { id: brandDoc.id, ...(brandDoc.data() || {}) }
        }

        // TODO: update sounds to support all platforms and support adding multiple sounds together
        const { soundId, showSoundTopPostsOnDashboard } = firstBrandCampaign
        if (soundId) {
          const submissionType = getSubmissionTypeFromCampaign(firstBrandCampaign)
          const externalPlatform = getExternalPlatformBySubmittableType(submissionType)

          // TODO: Update this conditional when we set up IG sound tracking
          if (submissionType === CAMPAIGN_SUBMITTABLE_TYPES.TIKTOK_POST) {
            commit('SET_EXTERNAL_TRACK_LOADING', true)
            const soundDoc = await getDoc(EXTERNAL_PLATFORM_TRACK_DOC_PATH(externalPlatform, soundId))
            const soundData = { id: soundDoc.id,  ...(soundDoc.data() || {}) }
            result.soundData = filterObjectKeys(soundData, [
              'displayName',
              'coverImageUrl',
              'dashboardName',
              'authorName',
              'latestRefreshStatsHourlyUntilDate',
              'showSoundTopPostsOnDashboard',
              'showSoundContentTrendAnalysisOnDashboard',
              'soundPostPerformanceSuccessCount',
              'aiContentTrendAnalysis',
            ])

            ;(async () => {
              const aggregateSoundCohortStats = filterObjectKeys(soundData, ['createCount', 'viewCount', 'likeCount', 'commentCount', 'shareCount'])
              const externalTrackStatLogs = [
                ...await getExternalTrackHistoryInner(externalPlatform, soundId),
                {
                  logTiming: {
                    date: new Date(),
                  },
                  ...aggregateSoundCohortStats,
                },
              ]
              if (externalTrackStatLogs) commit('SET_EXTERNAL_TRACK_DATA', ensureUpAndToTheRight(externalTrackStatLogs))
              if (aggregateSoundCohortStats) commit('SET_SOUND_COHORT_STATS', aggregateSoundCohortStats)
              commit('SET_EXTERNAL_TRACK', soundData)
              commit('SET_EXTERNAL_TRACK_LOADING', false)

              const { soundPostPerformanceRefreshKey } = soundData
              if (showSoundTopPostsOnDashboard && soundPostPerformanceRefreshKey) {
                dispatch('fetchSoundTopPosts', { tiktokSoundId: soundId, soundPostPerformanceRefreshKey })
              }
            })()
          }
        }

        return result
      })()


      // Get top ten on and off platform submissions across the campaigns
      // There are two on-platform queries (one for each cohort) because one 'in' clause is allowed per query
      const topTenOnPlatformApprovedSubmissionsPromise = getCol(BRAND_CAMPAIGN_USER_RELATIONSHIPS_COL_PATH, q => q
        .where('dashboardSortScore', '>', 0)
        .where('brandCampaignId', 'in', brandCampaignIdList)
        .where('cohort', '==', 'submitted-approved')
        .orderBy('dashboardSortScore', 'desc'))

      const topTenOnPlatformCertifiedSubmissionsPromise = getCol(BRAND_CAMPAIGN_USER_RELATIONSHIPS_COL_PATH, q => q
        .where('dashboardSortScore', '>', 0)
        .where('brandCampaignId', 'in', brandCampaignIdList)
        .where('cohort', '==', 'submitted-approved-certified')
        .orderBy('dashboardSortScore', 'desc'))

      const topTenOffPlatformSubmissionsPromise = getCol(BRAND_CAMPAIGN_OFF_PLATFORM_RELATIONSHIPS_COL_PATH, q => q
        .where('dashboardSortScore', '>', 0)
        .where('brandCampaignId', 'in', brandCampaignIdList)
        .where('isSubmitted', '==', true)
        .orderBy('dashboardSortScore', 'desc'))

      const onPlatformDraftsPendingBrandReviewPromise = getCol(BRAND_CAMPAIGN_USER_RELATIONSHIPS_COL_PATH, q => q
        .where('brandCampaignId', 'in', brandCampaignIdList)
        .where('cohort', '==', 'applied-draft-brand-review'))

      const offPlatformDraftsPendingBrandReviewPromise = getCol(BRAND_CAMPAIGN_OFF_PLATFORM_RELATIONSHIPS_COL_PATH, q => q
        .where('brandCampaignId', 'in', brandCampaignIdList)
        .where('cohort', '==', 'applied-draft-brand-review'))

      await Promise.all(brandCampaignIdList.map(async (brandCampaignId) => {
        const brandCampaignDoc = await getDoc(BRAND_CAMPAIGN_DOC_PATH(brandCampaignId))
        const brandCampaign = { id: brandCampaignDoc.id, ...(brandCampaignDoc.data() || {}) }
        const { creationDate, chartStartDate, chartEndDate, statLogUpdateKey } = brandCampaign
        const olderThanMonth = ((date(chartEndDate)?.getTime() || Date.now()) - (date(chartStartDate)?.getTime() || date(creationDate)?.getTime() || 0)) > ONE_MONTHISH
        selectedStatLogHours = olderThanMonth ? TARGET_STAT_LOG_HOURS_BACKOFF : TARGET_STAT_LOG_HOURS

        if (!statLogUpdateKey) return

        const logDateQueryAdditionList = []
        if (chartStartDate) {
          logDateQueryAdditionList.push(q => q.where('logTiming.date', '>=', timestamp(chartStartDate)))
        }
        if (chartEndDate) {
          logDateQueryAdditionList.push(q => q.where('logTiming.date', '<=', timestamp(chartEndDate)))
        }

        // Query stat logs for every campaign
        const campaignStatLogCol = await getCol(BRAND_CAMPAIGN_DASHBOARD_STAT_LOGS_COL_PATH(brandCampaignId), q => logDateQueryAdditionList.reduce((q1, addition) => addition(q1), q)
          .where('logTiming.hourOfDay', 'in', selectedStatLogHours)
          .where('statLogUpdateKey', '==', statLogUpdateKey)
          .orderBy('logTiming.date', 'asc'))
        const campaignStatLogDocs = campaignStatLogCol.docs || []
        const campaignStatLogs = campaignStatLogDocs
          .map(doc => ({ id: doc.id, brandCampaignId, ...doc.data() || {} }))
        campaignStatLogList.push(campaignStatLogs)

        if (!chartEndDate) {
          allCurrentCampaignStats.push(...getRelevantStatsList(brandCampaign))
          allCurrentCampaignProjectionStats.push(...getRelevantStatsList(brandCampaign))
        } else if (campaignStatLogs.length) {
          allCurrentCampaignStats.push(...getRelevantStatsList(campaignStatLogs[campaignStatLogs.length - 1]))
          allCurrentCampaignProjectionStats.push(...getRelevantStatsList(campaignStatLogs[campaignStatLogs.length - 1]))
        }
      }))

      // Accumulate cohort stats
      const aggregateCohortCampaignStats = aggregateCurrentStatList(allCurrentCampaignStats)
      const aggregateCohortProjectionStats = aggregateCurrentStatList(allCurrentCampaignProjectionStats)

      // Accumulate stat logs
      const aggregateCampaignStatLogs = []
      const sortedAllLogs = campaignStatLogList
        .flat()
        .filter(({ logTiming = {} }) => logTiming.hourNumber)
        .sort((a,b) => a.logTiming.hourNumber - b.logTiming.hourNumber)

      if (sortedAllLogs.length) {
        const minHourNumber = sortedAllLogs[0].logTiming.hourNumber
        const maxHourNumber = sortedAllLogs[sortedAllLogs.length - 1].logTiming.hourNumber

        for (let hourNumber = minHourNumber; hourNumber <= maxHourNumber; hourNumber += 1) {
          const hourStartDate = statHourStartDate(hourNumber)
          if (selectedStatLogHours.includes(hourNumber % 24) && hourStartDate < new Date()) {
            const logsToAdd = brandCampaignIdList
              .map((brandCampaignId) => {
                return sortedAllLogs
                  .filter(log => log.brandCampaignId === brandCampaignId)
                  .filter(({ logTiming = {} }) => logTiming.hourNumber <= hourNumber)
                  .reverse()[0]
              })
              .filter(log => log)
            const newDataPoint = aggregateCurrentStatList(logsToAdd.map(log => aggregateCurrentStatList(getRelevantStatsList(log))))
            aggregateCampaignStatLogs.push({ ...newDataPoint, logTiming: { date: hourStartDate } })
          }
        }
      }

      aggregateCampaignStatLogs.push({
        logTiming: {
          date: new Date(),
        },
        ...aggregateCohortCampaignStats,
      })

      const coverInfo = (await coverInfoPromise) || {}
      const topTenOnPlatformApprovedSubmissionDocs = (await topTenOnPlatformApprovedSubmissionsPromise).docs || []
      const topTenOnPlatformCertifiedSubmissionDocs = (await topTenOnPlatformCertifiedSubmissionsPromise).docs || []
      const topTenOffPlatformSubmissionDocs = (await topTenOffPlatformSubmissionsPromise).docs || []
      const onPlatformDraftsPendingBrandReviewDocs = (await onPlatformDraftsPendingBrandReviewPromise).docs || []
      const offPlatformDraftsPendingBrandReviewDocs = (await offPlatformDraftsPendingBrandReviewPromise).docs || []

      const topSubmissionsList = [...topTenOnPlatformApprovedSubmissionDocs, ...topTenOnPlatformCertifiedSubmissionDocs, ...topTenOffPlatformSubmissionDocs]
        .map(doc => ({ id: doc.id, ...(doc.data() || {}) }))
        // Just in case hideFromDashboard and dashboardSortScore get out of sync
        // Also filter out any submissions that don't have submissionData propagated
        .filter(({ hideFromDashboard, submissionData }) => !hideFromDashboard && (submissionData !== undefined))
        .filter((relationship, i, a) => a.map(getUniqueSubmissionString).findIndex(uss => uss === getUniqueSubmissionString(relationship)) === i)
        .sort((a, b) => (b.dashboardSortScore - a.dashboardSortScore) || a.id.localeCompare(b.id))

      const onPlatformDrafts = onPlatformDraftsPendingBrandReviewDocs.map(doc => ({ id: doc.id, ...(doc.data() || {}) }))
      const offPlatfomDrafts = offPlatformDraftsPendingBrandReviewDocs.map(doc => ({ id: doc.id, isOffPlatform: true, ...(doc.data() || {}) }))

      const draftsPendingBrandReview = [...onPlatformDrafts, ...offPlatfomDrafts]
        .sort((a, b) => (date(b.draftUploadDate)?.getTime() || 0) - (date(a.draftUploadDate)?.getTime() || 0))

      commit('SET_DASHBOARD_LOADING', false)
      commit('SET_COVER_INFO', coverInfo)
      commit('SET_CAMPAIGN_COHORT_STATS', aggregateCohortCampaignStats)
      commit('SET_COHORT_PROJECTION_STATS', aggregateCohortProjectionStats)
      commit('SET_CAMPAIGN_STAT_LOGS', ensureUpAndToTheRight(aggregateCampaignStatLogs))
      commit('SET_TOP_SUBMISSIONS_LIST', topSubmissionsList)
      commit('SET_DRAFTS_PENDING_BRAND_REVIEW', draftsPendingBrandReview)
    },
    async getCoverInfoForReport(_, { report }) {
      let {
        title: fullName,
        subtitle,
        profilePhotoPath,
        brandCampaignIdList = [],
        soundParamsList = [],
      } = report

      if (!fullName || !profilePhotoPath) {
        const [firstBrandCampaignId] = brandCampaignIdList
        if (firstBrandCampaignId) {
          const firstBrandCampaignDoc = await getDoc(BRAND_CAMPAIGN_DOC_PATH(firstBrandCampaignId))
          const firstBrandCampaign = { id: firstBrandCampaignDoc.id, ...(firstBrandCampaignDoc.data() || {}) }

          if (!fullName) {
            fullName = firstBrandCampaign.dashboardName || firstBrandCampaign.fullName
            if (fullName) {
              subtitle = subtitle || (firstBrandCampaign.sponsorName ? `sponsored by ${firstBrandCampaign.sponsorName}` : undefined)
            }
          }

          profilePhotoPath = profilePhotoPath
            || firstBrandCampaign.profilePhotoPath
        }
      }

      if (!fullName || !profilePhotoPath) {
        const [{ platform, soundId } = {}] = soundParamsList
        if (platform && soundId) {
          const firstSoundDoc = await getDoc(EXTERNAL_PLATFORM_TRACK_DOC_PATH(platform, soundId))
          const firstSound = { id: firstSoundDoc.id, ...(firstSoundDoc.data() || {}) }

          if (!fullName) {
            fullName = firstSound.displayName
            if (fullName) {
              subtitle = subtitle || (firstSound.authorName ? `by ${firstSound.authorName}` : undefined)
            }
          }

          profilePhotoPath = profilePhotoPath
            || firstSound.profilePhotoPath
        }
      }

      return {
        success: true,
        fullName,
        profilePhotoPath,
        subtitle,
        ...filterObjectKeys(report, [
          'chartStartDate',
          'chartEndDate',
          'showDashboardProjections',
          'showTopPostsOnDashboard',
          'showSoundGraph',
          'showSoundTopPostsOnDashboard',
          'showSoundContentTrendAnalysisOnDashboard',
        ]),
      }
    },
    async getReportData({ commit, dispatch }, { reportId }) {
      commit('SET_DASHBOARD_LOADING', true)

      const reportDoc = await getDoc(REPORT_DOC_PATH(reportId))
      const report = { id: reportDoc.id, ...(reportDoc.data() || {}) }

      const { brandCampaignIdList = [] } = report
      let { soundParamsList = [] } = report

      const currentCampaignStatsList = []
      const currentCampaignProjectionStatsList = []
      const campaignStatLogListList = []

      const currentSoundStatsList = []
      const soundStatLogListList = []

      const TARGET_STAT_LOG_HOURS = [3, 9, 15, 21]
      const TARGET_STAT_LOG_HOURS_BACKOFF = [3, 15]

      const promiseList = []

      promiseList.push(
        (async () => {
          const { success, ...coverInfo } = await dispatch('getCoverInfoForReport', { report })
          if (!success) return { success: false }
          commit('SET_COVER_INFO', coverInfo)
          return { success: true }
        })()
      )

      const brandCampaignList = await Promise.all(
        brandCampaignIdList.map(async (brandCampaignId) => {
          const brandCampaignDoc = await getDoc(BRAND_CAMPAIGN_DOC_PATH(brandCampaignId))
          return { id: brandCampaignDoc.id, ...(brandCampaignDoc.data() || {}) }
        })
      )

      commit('SET_BRAND_CAMPAIGN_LIST', brandCampaignList)

      brandCampaignList.forEach((brandCampaign) => {
        const { soundId } = brandCampaign
        const submissionType = getSubmissionTypeFromCampaign(brandCampaign)
        const platform = getExternalPlatformBySubmittableType(submissionType)
        if (soundId && platform) {
          soundParamsList.push({ platform, soundId })
        }
      })

      const soundList = await Promise.all(
        soundParamsList
          .filter(({ platform, soundId }, i, a) => platform && soundId && a.findIndex(s => s.platform === platform && s.soundId === soundId) === i)
          .map(async ({ platform, soundId }) => {
            if (!platform || !soundId) return undefined
            const soundDoc = await getDoc(EXTERNAL_PLATFORM_TRACK_DOC_PATH(platform, soundId))
            return { id: soundDoc.id, platform, soundId, ...(soundDoc.data() || {}) }
          })
      )

      commit('SET_SOUND_LIST', soundList)

      const {
        showTopPostsOnDashboard,
        showSoundTopPostsOnDashboard,
      } = report

      if (showTopPostsOnDashboard && brandCampaignIdList.length) {
        promiseList.push(
          (async () => {
            // Get approved on and off platform submissions across the campaigns
            // There are two on-platform queries (one for each cohort) because one 'in' clause is allowed per query
            const onPlatformApprovedSubmissionsPromise = getCol(BRAND_CAMPAIGN_USER_RELATIONSHIPS_COL_PATH, q => q
              .where('dashboardSortScore', '>', 0)
              .where('brandCampaignId', 'in', brandCampaignIdList)
              .where('cohort', '==', 'submitted-approved')
              .orderBy('dashboardSortScore', 'desc'))

            const onPlatformCertifiedSubmissionsPromise = getCol(BRAND_CAMPAIGN_USER_RELATIONSHIPS_COL_PATH, q => q
              .where('dashboardSortScore', '>', 0)
              .where('brandCampaignId', 'in', brandCampaignIdList)
              .where('cohort', '==', 'submitted-approved-certified')
              .orderBy('dashboardSortScore', 'desc'))

            const offPlatformSubmissionsPromise = getCol(BRAND_CAMPAIGN_OFF_PLATFORM_RELATIONSHIPS_COL_PATH, q => q
              .where('dashboardSortScore', '>', 0)
              .where('brandCampaignId', 'in', brandCampaignIdList)
              .where('isSubmitted', '==', true)
              .orderBy('dashboardSortScore', 'desc'))

            const onPlatformApprovedSubmissionDocs = (await onPlatformApprovedSubmissionsPromise).docs || []
            const onPlatformCertifiedSubmissionDocs = (await onPlatformCertifiedSubmissionsPromise).docs || []
            const offPlatformSubmissionDocs = (await offPlatformSubmissionsPromise).docs || []
      
            const topSubmissionsList = [...onPlatformApprovedSubmissionDocs, ...onPlatformCertifiedSubmissionDocs, ...offPlatformSubmissionDocs]
              .map(doc => ({ id: doc.id, ...(doc.data() || {}) }))
              // Just in case hideFromDashboard and dashboardSortScore get out of sync
              // Also filter out any submissions that don't have submissionData propagated
              .filter(({ hideFromDashboard, submissionData }) => !hideFromDashboard && (submissionData !== undefined))
              .sort((a, b) => (b.dashboardSortScore - a.dashboardSortScore) || a.id.localeCompare(b.id))

            commit('SET_TOP_SUBMISSIONS_LIST', topSubmissionsList)
            return { success: true }
          })()
        )
      }

      if (brandCampaignIdList.length) {
        promiseList.push(
          (async () => {
            const onPlatformDraftsPendingBrandReviewPromise = getCol(BRAND_CAMPAIGN_USER_RELATIONSHIPS_COL_PATH, q => q
              .where('brandCampaignId', 'in', brandCampaignIdList)
              .where('cohort', '==', 'applied-draft-brand-review'))

            const offPlatformDraftsPendingBrandReviewPromise = getCol(BRAND_CAMPAIGN_OFF_PLATFORM_RELATIONSHIPS_COL_PATH, q => q
              .where('brandCampaignId', 'in', brandCampaignIdList)
              .where('cohort', '==', 'applied-draft-brand-review'))


            const onPlatformDraftsPendingBrandReviewDocs = (await onPlatformDraftsPendingBrandReviewPromise).docs || []
            const offPlatformDraftsPendingBrandReviewDocs = (await offPlatformDraftsPendingBrandReviewPromise).docs || []

            const onPlatformDrafts = onPlatformDraftsPendingBrandReviewDocs.map(doc => ({ id: doc.id, ...(doc.data() || {}) }))
            const offPlatfomDrafts = offPlatformDraftsPendingBrandReviewDocs.map(doc => ({ id: doc.id, isOffPlatform: true, ...(doc.data() || {}) }))

            const draftsPendingBrandReview = [...onPlatformDrafts, ...offPlatfomDrafts]
              .sort((a, b) => (date(b.draftUploadDate)?.getTime() || 0) - (date(a.draftUploadDate)?.getTime() || 0))

            commit('SET_DRAFTS_PENDING_BRAND_REVIEW', draftsPendingBrandReview)
            return { success: true }
          })()
        )
      }

      await Promise.all(
        [
          ...brandCampaignList.map(async (brandCampaign) => {
            const { creationDate, chartStartDate, chartEndDate, statLogUpdateKey } = brandCampaign
            const olderThanMonth = ((date(chartEndDate)?.getTime() || Date.now()) - (date(chartStartDate)?.getTime() || date(creationDate)?.getTime() || 0)) > ONE_MONTHISH
            const selectedStatLogHours = olderThanMonth ? TARGET_STAT_LOG_HOURS_BACKOFF : TARGET_STAT_LOG_HOURS

            const logDateQueryAdditionList = []
            if (chartStartDate) {
              logDateQueryAdditionList.push(q => q.where('logTiming.date', '>=', timestamp(chartStartDate)))
            }
            if (chartEndDate) {
              logDateQueryAdditionList.push(q => q.where('logTiming.date', '<=', timestamp(chartEndDate)))
            }

            // Query stat logs for every campaign
            const campaignStatLogCol = await getCol(BRAND_CAMPAIGN_DASHBOARD_STAT_LOGS_COL_PATH(brandCampaign.id), q => logDateQueryAdditionList.reduce((q1, addition) => addition(q1), q)
              .where('logTiming.hourOfDay', 'in', selectedStatLogHours)
              .where('statLogUpdateKey', '==', statLogUpdateKey)
              .orderBy('logTiming.date', 'asc'))
            const campaignStatLogDocs = campaignStatLogCol.docs || []
            const relevantCampaignStatLogList = campaignStatLogDocs
              .map(doc => ({ id: doc.id, brandCampaignId: brandCampaign.id, ...doc.data() || {} }))
              .map(({ logTiming, ...rest }) => ({ logTiming, ...aggregateCurrentStatList(getRelevantStatsList(rest)) }))
            campaignStatLogListList.push(relevantCampaignStatLogList)

            if (!chartEndDate) {
              currentCampaignStatsList.push(...getRelevantStatsList(brandCampaign))
              currentCampaignProjectionStatsList.push(...getRelevantStatsList(brandCampaign))
            } else if (relevantCampaignStatLogList.length) {
              currentCampaignStatsList.push(relevantCampaignStatLogList[relevantCampaignStatLogList.length - 1])
              currentCampaignProjectionStatsList.push(relevantCampaignStatLogList[relevantCampaignStatLogList.length - 1])
            }
          }),
          ...soundList.map(async (sound, i) => {
            if (i > 0) return // TODO support multiple sounds

            const cohortStats = filterObjectKeys(sound, ['createCount', 'viewCount', 'likeCount', 'commentCount', 'shareCount'])
            currentSoundStatsList.push(cohortStats)

            const statLogs = await getExternalTrackHistoryInner(sound.platform, sound.soundId)
            soundStatLogListList.push(statLogs)

            const { soundPostPerformanceRefreshKey } = sound
            if (showSoundTopPostsOnDashboard && soundPostPerformanceRefreshKey) {
              // TODO: this won't work for multiple sounds
              dispatch('fetchSoundTopPosts', { tiktokSoundId: sound.soundId, soundPostPerformanceRefreshKey })
            }
          }),
        ]
      )

      // Accumulate cohort stats
      const aggregatedCampaignStats = aggregateCurrentStatList(currentCampaignStatsList)
      const aggregatedProjectionStats = aggregateCurrentStatList(currentCampaignProjectionStatsList)

      // Accumulate stat logs
      const aggregatedCampaignStatLogs = [
        ...aggregateHistoricalStatListList(campaignStatLogListList, TARGET_STAT_LOG_HOURS),
        {
          logTiming: {
            date: new Date(),
          },
          ...aggregatedCampaignStats,
        },
      ]

      // Accumulate sound stats
      const aggregatedSoundStats = aggregateCurrentStatList(currentSoundStatsList)

      const aggregatedSoundStatLogs = [
        ...aggregateHistoricalStatListList(soundStatLogListList, TARGET_STAT_LOG_HOURS),
        {
          logTiming: {
            date: new Date(),
          },
          ...aggregatedSoundStats,
        },
      ]

      const promiseResults = await Promise.all(promiseList)
      const failedPromiseResults = promiseResults.filter(result => !result.success)
      if (failedPromiseResults.length) {
        return { success: false, error: failedPromiseResults[0].error || 'Failed to get dashboard data.' }
      }

      commit('SET_SHOW_CAMPAIGN_UI', brandCampaignList.length > 0)
      commit('SET_SHOW_SOUND_UI', soundList.length > 0)
      commit('SET_DASHBOARD_LOADING', false)
      commit('SET_CAMPAIGN_COHORT_STATS', aggregatedCampaignStats)
      commit('SET_COHORT_PROJECTION_STATS', aggregatedProjectionStats)
      commit('SET_CAMPAIGN_STAT_LOGS', ensureUpAndToTheRight(aggregatedCampaignStatLogs))
      commit('SET_SOUND_COHORT_STATS', aggregatedSoundStats)
      commit('SET_SOUND_STAT_LOGS', ensureUpAndToTheRight(aggregatedSoundStatLogs))

      return { success: true }
    },
    async getBrandFeedback({ commit }, { brandCampaignId }) {
      const unsub = watchCol(`${BRAND_CAMPAIGN_DOC_PATH(brandCampaignId)}/brandSubmissionFeedback`, q => q, (feedbackSnap) => {
        const feedbackDocs = feedbackSnap.docs || []
        const submissionFeedbackData = feedbackDocs.map((doc) => ({ id: doc.id, ...doc.data() || {} }))
        commit('SET_FEEDBACK_DATA', submissionFeedbackData)
      })
      commit('SET_FEEDBACK_UNSUBSCRIBE', unsub)
    },
    // eslint-disable-next-line no-unused-vars
    async checkIfTrackingSound({ commit }, { tiktokSoundId }) {
      const soundDoc = await getDoc(EXTERNAL_PLATFORM_TRACK_DOC_PATH(TIKTOK_NATIVE, tiktokSoundId))
      if (!soundDoc.exists) return false
      const soundData = soundDoc.data() || {}
      if (!soundData.trackingEnabled) return false

      return true
    },
    async getSoundDashboardData({ commit, dispatch }, { tiktokSoundId }) {
      // Get sound cover data
      const soundDoc = await getDoc(EXTERNAL_PLATFORM_TRACK_DOC_PATH(TIKTOK_NATIVE, tiktokSoundId))
      const soundData = soundDoc.data() || {}
      const cohortStats = filterObjectKeys(soundData, ['createCount', 'viewCount', 'likeCount', 'commentCount', 'shareCount'])
      commit('SET_COVER_INFO', {
        soundData: filterObjectKeys(soundData, [
          'displayName',
          'coverImageUrl',
          'dashboardName',
          'authorName',
          'latestRefreshStatsHourlyUntilDate',
          'showSoundTopPostsOnDashboardManual',
          'showSoundContentTrendAnalysisOnDashboardManual',
          'soundPostPerformanceSuccessCount',
          'aiContentTrendAnalysis',
        ]),
      })
      commit('SET_SOUND_COHORT_STATS', cohortStats)

      const statLogs = [
        ...await getExternalTrackHistoryInner(TIKTOK_NATIVE, tiktokSoundId),
        { // Add the current snapshot as the latest data point
          logTiming: {
            date: new Date(),
          },
          ...cohortStats,
        },
      ]

      commit('SET_SOUND_STAT_LOGS', ensureUpAndToTheRight(statLogs))

      const { showSoundTopPostsOnDashboard, soundPostPerformanceRefreshKey } = soundData
      if (showSoundTopPostsOnDashboard && soundPostPerformanceRefreshKey) {
        dispatch('fetchSoundTopPosts', { tiktokSoundId, soundPostPerformanceRefreshKey })
      }
    },
    async fetchSoundTopPosts({ commit }, { tiktokSoundId, soundPostPerformanceRefreshKey }) {
      const postsSnap = await getCol(EXTERNAL_POSTS_COL_PATH(TIKTOK_NATIVE), q => 
        q.where('music.id', '==', tiktokSoundId)
          .where('latestSoundPostPerformanceRefreshKeyList', 'array-contains', soundPostPerformanceRefreshKey)
          .orderBy('performanceStats.overallEngagementOverAuthorMedian', 'desc')
          .limit(100)
      )

      const soundTopPostList = postsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
      
      // --- REMOVED TEMPORARY LOGGING ---
      // console.log('[Vuex fetchSoundTopPosts] Raw fetched list:', JSON.parse(JSON.stringify(soundTopPostList)))
      // if (soundTopPostList.length > 0) {
      //   console.log('[Vuex fetchSoundTopPosts] First post stats:', JSON.stringify(soundTopPostList[0].stats))
      //   console.log('[Vuex fetchSoundTopPosts] First post viewCount:', soundTopPostList[0].stats?.viewCount)
      // }
      // --- REMOVED TEMPORARY LOGGING ---

      commit('SET_SOUND_TOP_POST_LIST', soundTopPostList)
    },
  },
}
